import { useState } from "react";
import { useOrganizationChildren } from "../api";
import type { Organization } from "../types";

interface OrganizationTreeNodeProps {
  organization: Organization;
  depth: number;
  selectedId: string | null;
  onSelect: (org: Organization) => void;
}

export function OrganizationTreeNode({
  organization,
  depth,
  selectedId,
  onSelect,
}: OrganizationTreeNodeProps) {
  const [expanded, setExpanded] = useState(false);
  const { data: children, isLoading } = useOrganizationChildren(
    expanded ? organization.id : null,
  );

  const isSelected = selectedId === organization.id;

  return (
    <div style={{ marginBottom: "2px" }}>
      <div
        style={{
          paddingInlineStart: `${depth * 24}px`,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          borderRadius: "8px",
          padding: "6px 8px",
          cursor: "pointer",
          backgroundColor: isSelected ? "#eff6ff" : "transparent",
          transition: "background-color 0.2s ease",
          boxShadow: isSelected ? "inset 0 0 0 1px #bfdbfe" : "none",
        }}
        onMouseEnter={(e) => {
          if (!isSelected) e.currentTarget.style.backgroundColor = "#f9fafb";
        }}
        onMouseLeave={(e) => {
          if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
        }}
      >
        <button
          type="button"
          aria-label={expanded ? "جمع کردن" : "باز کردن"}
          onClick={() => setExpanded((v) => !v)}
          style={{
            width: "24px",
            height: "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            color: "#6b7280",
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            borderRadius: "4px",
            transition: "all 0.2s"
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e5e7eb"}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
        >
          {expanded ? "▼" : "◀"}
        </button>
        <span 
          onClick={() => onSelect(organization)} 
          style={{ flex: 1, fontSize: "0.95rem", color: isSelected ? "#1d4ed8" : "#374151", fontWeight: isSelected ? 600 : 400 }}
        >
          {organization.name}{" "}
          <span style={{ fontSize: "0.8rem", color: "#9ca3af", fontWeight: 400 }}>({organization.code})</span>
          {organization.status === "archived" && (
            <span style={{ marginLeft: "8px", marginRight: "8px", fontSize: "0.75rem", color: "#ef4444", backgroundColor: "#fee2e2", padding: "2px 6px", borderRadius: "12px" }}>
              آرشیو شده
            </span>
          )}
        </span>
      </div>

      {expanded && (
        <div style={{ marginTop: "4px" }}>
          {isLoading && (
            <div style={{ paddingInlineStart: `${(depth + 1) * 24 + 32}px`, fontSize: "0.8rem", color: "#9ca3af", padding: "4px 0" }}>
              در حال بارگذاری...
            </div>
          )}
          {!isLoading && children && children.length === 0 && (
            <div style={{ paddingInlineStart: `${(depth + 1) * 24 + 32}px`, fontSize: "0.8rem", color: "#9ca3af", padding: "4px 0", fontStyle: "italic" }}>
              زیرمجموعه‌ای ندارد
            </div>
          )}
          {!isLoading &&
            children?.map((child: Organization) => (
              <OrganizationTreeNode
                key={child.id}
                organization={child}
                depth={depth + 1}
                selectedId={selectedId}
                onSelect={onSelect}
              />
            ))}
        </div>
      )}
    </div>
  );
}
